// The city picker's last card, "Request a city": a lit pixel billboard like the ones in the scenes, in their own font,
// on a scaffold with three floodlamps. As the picker's grid opens its marquee bulbs chase and its face rolls between two
// pages, twice through, then it rests on the first page with every bulb lit; the second line takes the city's accent
// (site/js/app.js starts and stops it). Reduced motion, or Settings' Motion on Still (`still`): lit and still from the start.
import { makeCanvas, ditherFill, makeHalo, drawHalo, rect, text, textWidth } from '../engine/core.js';

const W = 80, H = 45;
const FACE = { x: 15, y: 10, w: 50, h: 18 };
const FRAME = { x0: FACE.x - 1, y0: FACE.y - 1, x1: FACE.x + FACE.w, y1: FACE.y + FACE.h };
const PAGES = [['YOUR CITY', 'HERE'], ['REQUEST', 'WITH COFFEE']];
const LAMPS = [25, 40, 55];
const INK = '#f3ead6', STEEL = '#2a2f4a', STEEL_LIT = '#454c6e', DARK = '#161a2c';
const BULB = '#ffe7a3', BULB_OFF = '#5c4b30', LAMP = '#fff3c4';
const PAGE_MS = 3200, ROLL_MS = 320, CHASE_MS = 180, TICK_MS = 60;
export const REST_MS = 2 * PAGES.length * PAGE_MS;    // both pages twice, then it stands still (css .req-art canvas glows as long)

// a bulb on every 3rd pixel of the frame, clockwise from the top-left corner
const BULBS = [];
for (let x = FRAME.x0 + 1; x < FRAME.x1; x += 3) BULBS.push([x, FRAME.y0]);
for (let y = FRAME.y0 + 2; y < FRAME.y1; y += 3) BULBS.push([FRAME.x1, y]);
for (let x = FRAME.x1 - 1; x > FRAME.x0; x -= 3) BULBS.push([x, FRAME.y1]);
for (let y = FRAME.y1 - 2; y > FRAME.y0; y -= 3) BULBS.push([FRAME.x0, y]);

function buildStatic() {
  const [c, x] = makeCanvas(W, H);
  for (const lx of [24, 55]) { rect(x, lx, FRAME.y1 + 1, 2, H - FRAME.y1 - 1, DARK); rect(x, lx, FRAME.y1 + 1, 1, H - FRAME.y1 - 1, STEEL); }
  for (let i = 0; i <= 29; i++) {                      // the X brace between the legs
    const y = FRAME.y1 + 5 + Math.round(i * 10 / 29);
    rect(x, 26 + i, y, 1, 1, DARK);
    rect(x, 54 - i, y, 1, 1, DARK);
  }
  rect(x, 12, FRAME.y1 + 2, 56, 1, STEEL_LIT);         // catwalk rail and floor
  rect(x, 12, FRAME.y1 + 3, 56, 1, STEEL);
  rect(x, FRAME.x0, FRAME.y0, FRAME.x1 - FRAME.x0 + 1, FRAME.y1 - FRAME.y0 + 1, STEEL);
  for (const lx of LAMPS) { rect(x, lx, FRAME.y0 - 3, 1, 3, STEEL_LIT); rect(x, lx - 1, FRAME.y0 - 4, 3, 1, LAMP); }
  return c;
}

function buildPages(accent) {
  const back = ditherFill(FACE.w, FACE.h, ['#1c2046', '#0d0f24'], (i, j) => j / 5);   // lit along the top edge
  const y0 = Math.round((FACE.h - 12) / 2);
  return PAGES.map(lines => {
    const [c, x] = makeCanvas(FACE.w, FACE.h);
    x.drawImage(back, 0, 0);
    lines.forEach((s, k) => text(x, s, Math.round((FACE.w - textWidth(s)) / 2), y0 + k * 7, k ? accent : INK));
    return c;
  });
}

export function createRequestBoard({ still = () => matchMedia('(prefers-reduced-motion: reduce)').matches } = {}) {
  const [canvas, ctx] = makeCanvas(W, H, true);        // an element: it goes on the page
  canvas.setAttribute('aria-hidden', 'true');
  const base = buildStatic(), halo = makeHalo(9, '#ffd98a', 0.3);
  let pages = null, accent = '', timer = 0, t0 = 0;

  function draw(t, lit = still()) {                    // lit: every bulb on (at rest, or reduced motion)
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(base, 0, 0);
    const i = Math.floor(t / PAGE_MS) % PAGES.length, into = t % PAGE_MS;
    const s = Math.round(Math.max(0, (into - (PAGE_MS - ROLL_MS)) / ROLL_MS) * FACE.h);   // the roll up to the next page
    const a = pages[i], b = pages[(i + 1) % PAGES.length];
    if (s < FACE.h) ctx.drawImage(a, 0, s, FACE.w, FACE.h - s, FACE.x, FACE.y, FACE.w, FACE.h - s);
    if (s > 0) ctx.drawImage(b, 0, 0, FACE.w, s, FACE.x, FACE.y + FACE.h - s, FACE.w, s);
    ctx.save();                                        // the floodlamps light the face only; the CSS glow does the air
    ctx.beginPath(); ctx.rect(FACE.x, FACE.y, FACE.w, FACE.h); ctx.clip();
    for (const lx of LAMPS) drawHalo(ctx, halo, lx, FACE.y + 1, 0.9);
    ctx.restore();
    const phase = Math.floor(t / CHASE_MS) % 3;
    BULBS.forEach(([x, y], k) => rect(ctx, x, y, 1, 1, lit || (k + phase) % 3 ? BULB : BULB_OFF));
  }
  function tick() {
    const t = performance.now() - t0;
    if (t < REST_MS) { draw(t); return; }
    clearInterval(timer);
    timer = 0;
    draw(0, true);                                     // at rest: the first page, every bulb lit
  }
  return {
    canvas,
    start(cityAccent) {
      if (cityAccent !== accent) { accent = cityAccent; pages = buildPages(accent); }
      clearInterval(timer);
      t0 = performance.now();
      draw(0);
      if (!still()) timer = setInterval(tick, TICK_MS);
    },
    stop() { clearInterval(timer); timer = 0; },
  };
}
